import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, X, Check, Copy, Building2, Search, Monitor } from "lucide-react";

const RUSTDESK_SERVER = "ayrshirehub.co.uk";
const RUSTDESK_KEY = "qgF1EQZwL+LfBIu5a4lKUuOw4Lto1bcLKtZN57QS6m0=";
const RUSTDESK_PASSWORD = "Hub990@@@";
import { ImageUpload } from "@/components/ImageUpload";
import { useCategories } from "@/hooks/use-categories";

const TOWNS = ["Ayr","Kilmarnock","Irvine","Prestwick","Troon","Saltcoats","Ardrossan","Largs","Kilwinning","Stewarton","Maybole","Girvan","Cumnock","Auchinleck","Newmilns","Kilbirnie","Beith","Dalry"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface Business {
  id: number;
  name: string;
  category: string;
  town: string;
  description: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  openingHours: string | null;
  imageUrl: string | null;
  approved: boolean;
  verified: boolean;
  featured: boolean;
  ownerId: number | null;
  ownerName: string | null;
  ownerEmail: string | null;
  rustdeskId: string | null;
  createdAt: string;
}

function BusinessPhoto({ name, imageUrl }: { name: string; imageUrl: string | null }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const source = imageUrl?.trim() || null;
  return (
    <div className="w-16 h-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      {source && failedSource !== source ? (
        <img src={source} alt={`${name} photo`} loading="lazy" className="h-full w-full object-cover" onError={() => setFailedSource(source)} />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1 px-1 text-center text-[10px] leading-tight text-slate-500">
          <Building2 size={18} aria-hidden="true" />
          <span>{source ? "Picture unavailable" : "Awaiting picture"}</span>
        </div>
      )}
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }
  return (
    <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-sm font-mono text-slate-800 font-semibold">{value}</p>
      </div>
      <button onClick={copy} className="text-slate-400 hover:text-slate-700 ml-3 flex-shrink-0" title="Copy">
        {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

function RemoteControlModal({ business, onClose }: { business: Business; onClose: () => void }) {
  const webClientUrl = `/remote/`;
  const appUrl = `rustdesk://connect/${business.rustdeskId ?? ""}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-slate-800 flex items-center gap-2"><Monitor size={18} /> Remote Control — {business.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <a href={webClientUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-primary text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
          <Monitor size={16} /> Open Web Remote
        </a>

        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
          <p className="font-semibold mb-1">Once the web client opens:</p>
          <p>Enter the Device ID below and password <span className="font-mono font-bold">{RUSTDESK_PASSWORD}</span> to connect.</p>
        </div>

        <div className="space-y-2">
          <CopyField label="Device ID — paste into web client" value={business.rustdeskId ?? ""} />
          <CopyField label="Password" value={RUSTDESK_PASSWORD} />
        </div>

        <div className="border-t border-slate-100 pt-3">
          <a href={appUrl}
            className="flex items-center justify-center gap-2 w-full border border-slate-200 text-slate-600 rounded-lg py-2 text-xs font-medium hover:bg-slate-50 transition-colors">
            <Monitor size={12} /> Or open in RustDesk desktop app
          </a>
        </div>

        <p className="text-xs text-slate-400 text-center">The tablet must have RustDesk open and connected.</p>
      </div>
    </div>
  );
}

interface UserResult { id: number; name: string; email: string; role: string; }

const EMPTY_FORM = {
  name: "", category: "", description: "", town: "Ayr", address: "", phone: "",
  email: "", website: "", facebook: "", instagram: "", openingHours: "", imageUrl: "",
  ownerId: "", ownerDisplay: "", verified: false, featured: false, rustdeskId: "",
};

function UserSearch({ value, display, onChange }: {
  value: string; display: string;
  onChange: (id: string, label: string) => void;
}) {
  const [q, setQ] = useState(display);
  const [results, setResults] = useState<UserResult[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setQ(display); }, [display]);

  const search = useCallback((s: string) => {
    setQ(s);
    if (timer.current) clearTimeout(timer.current);
    if (s.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/admin/users/search?q=${encodeURIComponent(s)}`, { headers: authHeaders() });
        const d = await r.json() as { users: UserResult[] };
        setResults(d.users ?? []);
        setOpen(true);
      } catch { /* ignore */ }
    }, 300);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={e => search(e.target.value)}
          onFocus={() => q.length >= 2 && results.length > 0 && setOpen(true)}
          placeholder="Search users by name or email…"
          className="h-8 text-sm pl-7"
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange("", ""); setQ(""); setResults([]); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={13} />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
          {results.map(u => (
            <button
              key={u.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
              onClick={() => { onChange(String(u.id), `${u.name} (${u.email})`); setQ(`${u.name} (${u.email})`); setOpen(false); }}
            >
              <span className="font-medium">{u.name}</span>
              <span className="text-slate-400 ml-2 text-xs">{u.email} · {u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BusinessForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: typeof EMPTY_FORM;
  onSave: (form: typeof EMPTY_FORM) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);
  const set = (k: string, v: string | boolean) => setForm(p => ({ ...p, [k]: v }));
  const businessCategories = useCategories("business");

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Business Name *</label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} className="h-8 text-sm" placeholder="e.g. The Corner Café" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Category *</label>
          <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background">
            <option value="">— Select category —</option>
            {businessCategories.map(c => <option key={c} value={c}>{c}</option>)}
            {form.category && !businessCategories.includes(form.category) && (
              <option value={form.category}>{form.category}</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Town *</label>
          <select value={form.town} onChange={e => set("town", e.target.value)} className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background">
            {TOWNS.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
          <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="h-8 text-sm" placeholder="01234 567890" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
          <Input value={form.email} onChange={e => set("email", e.target.value)} className="h-8 text-sm" type="email" placeholder="hello@business.com" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Website</label>
          <Input value={form.website} onChange={e => set("website", e.target.value)} className="h-8 text-sm" placeholder="https://…" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Facebook</label>
          <Input value={form.facebook} onChange={e => set("facebook", e.target.value)} className="h-8 text-sm" placeholder="facebook.com/…" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Instagram</label>
          <Input value={form.instagram} onChange={e => set("instagram", e.target.value)} className="h-8 text-sm" placeholder="@handle" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Address</label>
          <Input value={form.address} onChange={e => set("address", e.target.value)} className="h-8 text-sm" placeholder="1 High Street, Ayr, KA7 1AA" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Opening Hours</label>
          <Input value={form.openingHours} onChange={e => set("openingHours", e.target.value)} className="h-8 text-sm" placeholder="Mon–Fri 9am–5pm, Sat 10am–4pm" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
          <ImageUpload value={form.imageUrl ?? ""} onChange={url => set("imageUrl", url)} />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
          <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="text-sm" placeholder="Brief description of the business…" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">RustDesk Device ID <span className="font-normal text-slate-400">(tablet remote control)</span></label>
          <Input value={form.rustdeskId} onChange={e => set("rustdeskId", e.target.value)} className="h-8 text-sm font-mono" placeholder="e.g. 123 456 789" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Link to Registered User (Owner)</label>
          <UserSearch
            value={form.ownerId}
            display={form.ownerDisplay}
            onChange={(id, label) => setForm(p => ({ ...p, ownerId: id, ownerDisplay: label }))}
          />
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.verified} onChange={e => set("verified", e.target.checked)} className="rounded" />
            Verified
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} className="rounded" />
            Featured
          </label>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" disabled={saving || !form.name || !form.category || !form.town} onClick={() => void onSave(form)} className="bg-primary hover:bg-primary/90 h-8 text-xs px-4">
          {saving ? "Saving…" : "Save Business"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-8 text-xs px-3">Cancel</Button>
      </div>
    </div>
  );
}

export default function AdminBusinesses() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [remoteControlBusiness, setRemoteControlBusiness] = useState<Business | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/businesses", { headers: authHeaders() });
      const d = await r.json() as { items: Business[] };
      setBusinesses(d.items ?? []);
    } catch { toast({ title: "Failed to load businesses", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    if (!authLoading && user?.role === "admin") void load();
  }, [authLoading, user?.role, load]);

  const handleCreate = async (form: typeof EMPTY_FORM) => {
    setSaving(true);
    try {
      const r = await fetch("/api/admin/businesses", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ ...form, ownerId: form.ownerId || undefined }),
      });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Business created and approved" });
      setShowCreate(false);
      await load();
    } catch { toast({ title: "Failed to create business", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleEdit = async (id: number, form: typeof EMPTY_FORM) => {
    setSaving(true);
    try {
      const r = await fetch(`/api/businesses/${id}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({
          name: form.name, category: form.category, description: form.description || null,
          town: form.town, address: form.address || null, phone: form.phone || null,
          email: form.email || null, website: form.website || null,
          facebook: form.facebook || null, instagram: form.instagram || null,
          openingHours: form.openingHours || null, imageUrl: form.imageUrl || null,
          ownerId: form.ownerId ? Number(form.ownerId) : null,
          verified: form.verified, featured: form.featured,
          rustdeskId: form.rustdeskId || null,
        }),
      });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Business updated" });
      setEditingId(null);
      await load();
    } catch { toast({ title: "Failed to update business", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const toggleField = async (id: number, field: "approved" | "verified" | "featured", val: boolean) => {
    try {
      await fetch(`/api/businesses/${id}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({ [field]: val }),
      });
      setBusinesses(bs => bs.map(b => b.id === id ? { ...b, [field]: val } : b));
    } catch { toast({ title: "Failed to update", variant: "destructive" }); }
  };

  const handleMakeVenue = async (b: Business) => {
    const venueType = window.prompt(
      `Convert "${b.name}" to a venue.\n\nEnter venue type:\npub, bar, restaurant, nightclub, sports_bar, cocktail_bar, cafe, hotel`,
      b.category.toLowerCase().includes("restaurant") ? "restaurant"
        : b.category.toLowerCase().includes("night") ? "nightclub"
        : b.category.toLowerCase().includes("cafe") ? "cafe"
        : "pub"
    );
    if (!venueType) return;
    try {
      const r = await fetch(`/api/businesses/${b.id}/make-venue`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ venueType: venueType.trim() }),
      });
      const data = await r.json() as { error?: string; id?: number };
      if (!r.ok) { toast({ title: data.error ?? "Failed to convert", variant: "destructive" }); return; }
      toast({ title: `"${b.name}" added to What's On venues!`, description: "It will appear once active." });
    } catch { toast({ title: "Failed to convert", variant: "destructive" }); }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await fetch(`/api/businesses/${id}`, { method: "DELETE", headers: authHeaders() });
      setBusinesses(bs => bs.filter(b => b.id !== id));
      toast({ title: "Business deleted" });
    } catch { toast({ title: "Failed to delete", variant: "destructive" }); }
  };

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const filtered = businesses.filter(b => {
    if (!search) return true;
    const s = search.toLowerCase();
    return b.name.toLowerCase().includes(s) || b.town.toLowerCase().includes(s) || b.category.toLowerCase().includes(s) || (b.ownerName ?? "").toLowerCase().includes(s);
  });

  return (
    <>
    <AdminLayout current="/admin/businesses">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-primary">Businesses</h1>
            <p className="text-sm text-slate-500 mt-0.5">{businesses.length} total</p>
          </div>
          <Button onClick={() => { setShowCreate(true); setEditingId(null); }} className="bg-primary hover:bg-primary/90 h-8 text-xs px-4 gap-1.5">
            <Plus size={14} /> Add Business
          </Button>
        </div>

        {showCreate && (
          <Card>
            <CardContent className="pt-5">
              <h2 className="font-semibold text-slate-700 mb-4 flex items-center gap-2"><Building2 size={16} /> New Business</h2>
              <BusinessForm
                initial={EMPTY_FORM}
                onSave={handleCreate}
                onCancel={() => setShowCreate(false)}
                saving={saving}
              />
            </CardContent>
          </Card>
        )}

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by name, town, category, owner…"
            className="pl-8 h-8 text-sm"
          />
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-slate-400 text-sm">No businesses found.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(b => {
              const isEditing = editingId === b.id;
              const editInitial: typeof EMPTY_FORM = {
                name: b.name, category: b.category, description: b.description ?? "",
                town: b.town, address: b.address ?? "", phone: b.phone ?? "",
                email: b.email ?? "", website: b.website ?? "", facebook: b.facebook ?? "",
                instagram: b.instagram ?? "", openingHours: b.openingHours ?? "",
                imageUrl: b.imageUrl ?? "",
                ownerId: b.ownerId ? String(b.ownerId) : "",
                ownerDisplay: b.ownerName ? `${b.ownerName} (${b.ownerEmail ?? ""})` : "",
                verified: b.verified, featured: b.featured,
                rustdeskId: b.rustdeskId ?? "",
              };
              return (
                <Card key={b.id} className={isEditing ? "border-primary/40 shadow-sm" : ""}>
                  <CardContent className="py-3">
                    {!isEditing ? (
                      <div className="flex flex-wrap items-start gap-3">
                        <BusinessPhoto name={b.name} imageUrl={b.imageUrl} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800 truncate">{b.name}</span>
                            {b.approved && <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Approved</span>}
                            {!b.approved && <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Pending</span>}
                            {b.verified && <span className="text-xs px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">Verified</span>}
                            {b.featured && <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Featured</span>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{b.category} · {b.town}{b.ownerName ? ` · Owner: ${b.ownerName}` : " · No owner"}</p>
                          {b.phone && <p className="text-xs text-slate-400">{b.phone}</p>}
                        </div>
                        <div className="flex w-full items-center gap-1 flex-shrink-0 flex-wrap justify-end sm:w-auto">
                          <button
                            onClick={() => void toggleField(b.id, "approved", !b.approved)}
                            title={b.approved ? "Unapprove" : "Approve"}
                            className={`text-xs px-2 py-1 rounded border font-medium transition-colors ${b.approved ? "border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                          >
                            {b.approved ? "Unapprove" : "Approve"}
                          </button>
                          <button
                            onClick={() => void toggleField(b.id, "verified", !b.verified)}
                            title="Toggle verified"
                            className={`text-xs px-2 py-1 rounded border font-medium transition-colors ${b.verified ? "border-sky-200 text-sky-700 bg-sky-50" : "border-slate-200 text-slate-500 hover:border-sky-300"}`}
                          >
                            {b.verified ? <Check size={12} /> : "Verify"}
                          </button>
                          <button
                            onClick={() => { setEditingId(b.id); setShowCreate(false); }}
                            className="flex items-center gap-1 text-xs text-slate-500 font-medium px-2 py-1 rounded-lg border border-slate-200 hover:border-primary/40 hover:text-primary transition-colors"
                          >
                            <Pencil size={11} /> Edit
                          </button>
                          {b.rustdeskId && (
                            <button
                              onClick={() => setRemoteControlBusiness(b)}
                              title="Remote control tablet"
                              className="flex items-center gap-1 text-xs text-teal-700 font-medium px-2 py-1 rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors"
                            >
                              <Monitor size={11} /> Remote
                            </button>
                          )}
                          <button
                            onClick={() => void handleMakeVenue(b)}
                            title="Convert to What's On venue"
                            className="flex items-center gap-1 text-xs text-purple-600 font-medium px-2 py-1 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
                          >
                            <Building2 size={11} /> Venue
                          </button>
                          <button
                            onClick={() => void handleDelete(b.id, b.name)}
                            className="flex items-center gap-1 text-xs text-red-400 font-medium px-2 py-1 rounded-lg border border-red-100 hover:border-red-300 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-slate-700 text-sm">Editing: {b.name}</h3>
                          <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600"><X size={15} /></button>
                        </div>
                        <BusinessForm
                          initial={editInitial}
                          onSave={form => handleEdit(b.id, form)}
                          onCancel={() => setEditingId(null)}
                          saving={saving}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
    {remoteControlBusiness && (
      <RemoteControlModal business={remoteControlBusiness} onClose={() => setRemoteControlBusiness(null)} />
    )}
    </>
  );
}
